import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import SEO from '../components/SEO';
import {
  responsibilityDefinitions,
  responsibilityLabel,
  roleDefaults,
  roleDefinitions,
  roleLabel,
  visibleToUser
} from '../utils/rbacDefinitions';

const formatDate = (value) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const RBACPage = ({ user, onGoToColleges }) => {
  const canManageAccounts = user?.role === 'super_user' || user?.role === 'college_admin';
  const [users, setUsers] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(canManageAccounts);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [savingId, setSavingId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [verifyingId, setVerifyingId] = useState('');

  useEffect(() => {
    if (!canManageAccounts) return;

    const loadUsers = async () => {
      try {
        setLoading(true);
        setError('');
        const [usersRes, collegesRes] = await Promise.all([
          api.get('/users'),
          api.get('/colleges')
        ]);
        const visibleAccounts = usersRes.data.filter(account => visibleToUser(user, account));
        setUsers(visibleAccounts);
        setColleges(collegesRes.data);
        setSelectedUserId(current => current || visibleAccounts[0]?._id || '');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load account permissions');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [canManageAccounts, user]);

  const counts = useMemo(() => {
    return users.reduce((acc, account) => {
      acc.total += 1;
      acc[account.role] = (acc[account.role] || 0) + 1;
      acc.responsibilities += account.responsibilities?.length || 0;
      return acc;
    }, { total: 0, super_user: 0, college_admin: 0, teacher: 0, student: 0, responsibilities: 0 });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter(account => {
      const responsibilityLabels = (account.responsibilities || []).map(responsibilityLabel).join(' ').toLowerCase();
      const matchesRole = roleFilter === 'all' || account.role === roleFilter;
      const matchesSearch = !query ||
        account.name?.toLowerCase().includes(query) ||
        account.email?.toLowerCase().includes(query) ||
        (account.colleges || []).join(' ').toLowerCase().includes(query) ||
        responsibilityLabels.includes(query);
      return matchesRole && matchesSearch;
    });
  }, [roleFilter, search, users]);

  const selectedAccount = filteredUsers.find(account => account._id === selectedUserId) || filteredUsers[0];
  const availableRoles = user?.role === 'super_user'
    ? roleDefinitions.filter(role => role.value !== 'guest')
    : roleDefinitions.filter(role => role.value !== 'guest' && role.value !== 'super_user');
  const availableResponsibilities = user?.role === 'super_user'
    ? responsibilityDefinitions
    : responsibilityDefinitions.filter(item => !item.restrictedToSuperUser);

  const handleUpdateAccount = async (account, updates) => {
    const nextRole = updates.role ?? account.role;
    const nextCollege = updates.college ?? account.college ?? '';
    const nextResponsibilities = updates.responsibilities ?? account.responsibilities ?? [];

    if (account._id === user.id && nextRole !== account.role) {
      setError('You cannot change your own role from this page.');
      return;
    }

    try {
      setSavingId(account._id);
      setError('');
      setStatus('');
      const res = await api.put(`/users/${account._id}`, {
        role: nextRole,
        college: nextCollege,
        responsibilities: nextResponsibilities
      });
      if (!visibleToUser(user, res.data)) {
        setUsers(prev => prev.filter(item => item._id !== account._id));
        setSelectedUserId('');
      } else {
        setUsers(prev => prev.map(item => item._id === account._id ? res.data : item));
        setSelectedUserId(account._id);
      }
      setStatus(`${account.name}'s role and responsibilities were updated.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account access');
    } finally {
      setSavingId('');
    }
  };

  const handleResponsibilityToggle = (account, responsibility) => {
    const current = account.responsibilities || [];
    const next = current.includes(responsibility)
      ? current.filter(item => item !== responsibility)
      : [...current, responsibility];

    handleUpdateAccount(account, { responsibilities: next });
  };

  const applyRoleDefaults = (account) => {
    const defaults = roleDefaults[account.role] || [];
    const allowedDefaultResponsibilities = defaults.filter(item => availableResponsibilities.some(def => def.value === item));
    handleUpdateAccount(account, { responsibilities: allowedDefaultResponsibilities });
  };

  const handleVerifyEmail = async (account) => {
    try {
      setVerifyingId(account._id);
      setError('');
      const res = await api.put(`/users/${account._id}/verify-email`);
      setUsers(prev => prev.map(item => item._id === account._id ? { ...item, isVerified: true } : item));
      setStatus(`${account.name}'s email has been verified.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify email');
    } finally {
      setVerifyingId('');
    }
  };

  if (!canManageAccounts) {
    return (
      <div className="page-card">
        <h2>User Access Control</h2>
        <div className="error-message">You do not have permission to manage user access.</div>
      </div>
    );
  }

  return (
    <div className="page-card rbac-page">
      <SEO title="User Access Control" description="Manage user roles and permissions." />
      <div className="rbac-header">
        <div>
          <h2>User Access</h2>
          <p>Assign roles, college scope, and day-to-day responsibilities for each account.</p>
        </div>
        <div className="rbac-header-actions">
          {onGoToColleges && (
            <button type="button" className="secondary" onClick={onGoToColleges}>
              Manage Colleges
            </button>
          )}
          <span className="status-badge status-approved">{roleLabel(user.role)}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {status && <div className="status-message">{status}</div>}

      <div className="rbac-summary-grid">
        <div className="rbac-summary-card">
          <span>Total Accounts</span>
          <strong>{counts.total}</strong>
        </div>
        <div className="rbac-summary-card">
          <span>Admins</span>
          <strong>{counts.super_user + counts.college_admin}</strong>
        </div>
        <div className="rbac-summary-card">
          <span>Teachers</span>
          <strong>{counts.teacher}</strong>
        </div>
        <div className="rbac-summary-card">
          <span>Responsibilities</span>
          <strong>{counts.responsibilities}</strong>
        </div>
      </div>

      <section className="section">
        <div className="toolbar rbac-toolbar">
          <div>
            <h3>Assignment Console</h3>
            <p>{user.role === 'college_admin' ? 'Assign responsibilities inside your college scope.' : 'Assign roles and responsibilities across all colleges.'}</p>
          </div>
          <div className="rbac-filters">
            <input
              type="search"
              placeholder="Search accounts or responsibilities"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All roles</option>
              {availableRoles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="rbac-empty-state">Loading account controls...</div>
        ) : !selectedAccount ? (
          <div className="rbac-empty-state">No accounts are available for assignment.</div>
        ) : (
          <div className="rbac-assignment-layout">
            <div className="rbac-account-list" aria-label="Accounts">
              {filteredUsers.map(account => (
                <button
                  key={account._id}
                  type="button"
                  className={`rbac-account-row${selectedAccount?._id === account._id ? ' selected' : ''}`}
                  onClick={() => setSelectedUserId(account._id)}
                >
                  <span>
                    <strong>{account.name}</strong>
                    <small>{account.email}</small>
                  </span>
                  <span className="content-badge">{roleLabel(account.role)}</span>
                </button>
              ))}
            </div>

            <div className="rbac-assignment-panel">
              <div className="rbac-assignment-heading">
                <div>
                  <h3>{selectedAccount.name}</h3>
                  <p>{selectedAccount.email}</p>
                </div>
                {savingId === selectedAccount._id && <span className="content-badge">Saving</span>}
              </div>

              <div className="rbac-assignment-grid">
                <label>
                  Role
                  <select
                    value={selectedAccount.role}
                    disabled={selectedAccount._id === user.id || savingId === selectedAccount._id}
                    onChange={(e) => handleUpdateAccount(selectedAccount, { role: e.target.value })}
                  >
                    {availableRoles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </label>

                <label>
                  College Scope (Managed in Colleges page)
                  <div className="rbac-chip-list" style={{ marginTop: '8px' }}>
                    {(selectedAccount.colleges || []).length > 0 
                      ? selectedAccount.colleges.map(c => <span key={c}>{c}</span>)
                      : <span className="rbac-muted">No college</span>}
                  </div>
                </label>

                <div style={{ paddingTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <strong>Email Address</strong>
                    {selectedAccount.isVerified ? (
                      <span className="content-badge status-approved" style={{ fontSize: '12px' }}>✓ Verified</span>
                    ) : (
                      <span className="content-badge" style={{ fontSize: '12px', backgroundColor: '#ffa500' }}>Unverified</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>{selectedAccount.email}</span>
                    {!selectedAccount.isVerified && (
                      <button
                        type="button"
                        className="secondary"
                        style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}
                        onClick={() => handleVerifyEmail(selectedAccount)}
                        disabled={verifyingId === selectedAccount._id}
                      >
                        {verifyingId === selectedAccount._id ? 'Verifying...' : 'Verify Email'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="rbac-responsibility-header">
                <div>
                  <h4>Responsibilities</h4>
                  <p>Select the duties this person owns in day-to-day operations.</p>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => applyRoleDefaults(selectedAccount)}
                  disabled={savingId === selectedAccount._id}
                >
                  Apply Role Defaults
                </button>
              </div>

              <div className="rbac-responsibility-grid">
                {availableResponsibilities.map(responsibility => {
                  const checked = selectedAccount.responsibilities?.includes(responsibility.value) || false;
                  return (
                    <label key={responsibility.value} className={`rbac-responsibility-card${checked ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={savingId === selectedAccount._id}
                        onChange={() => handleResponsibilityToggle(selectedAccount, responsibility.value)}
                      />
                      <span>
                        <strong>{responsibility.label}</strong>
                        <small>{responsibility.description}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="section">
        <h3>Role Permission Matrix</h3>
        <div className="rbac-role-grid">
          {roleDefinitions
            .filter(role => user.role === 'super_user' || role.value !== 'super_user')
            .map(role => (
              <div key={role.value} className="rbac-role-card">
                <div className="rbac-role-card-header">
                  <h4>{role.label}</h4>
                  <span>{role.value}</span>
                </div>
                <p>{role.description}</p>
                <div className="rbac-permissions">
                  {role.permissions.map(permission => (
                    <span key={permission}>{permission}</span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
};

export default RBACPage;
